// Pure mutations on a WorkflowDefinition — used by the canvas (connect/delete)
// and the step editor. Ported from the prototype; cycle-safe.

import { ancestorsOf, findCycle } from "./graph";

/** Drop any outgoing edge that points at `deletedName`. */
export function clearNext(step, deletedName) {
  if (step.type === "endpoint_call") {
    return { ...step, next: (step.next || []).filter((n) => n !== deletedName) };
  }
  return {
    ...step,
    branches: (step.branches || []).filter((b) => b.next !== deletedName),
    default_next: step.default_next === deletedName ? null : step.default_next
  };
}

/** Add a fresh step; the first step also becomes start_step. Returns { def, name }. */
export function addStep(def, type) {
  const names = Object.keys(def.steps);
  let name =
    type === "branch" ? `branch_${names.length + 1}` : `step_${names.length + 1}`;
  while (def.steps[name]) name = `${name}_${Math.random().toString(36).slice(2, 5)}`;
  const step =
    type === "endpoint_call"
      ? { type: "endpoint_call", endpoint_id: "", request_payload: {}, next: [] }
      : { type: "branch", branches: [], default_next: null };
  return {
    def: {
      ...def,
      start_step: names.length === 0 ? name : def.start_step,
      steps: { ...def.steps, [name]: step }
    },
    name
  };
}

/** Remove a step + clear dangling references; reassign start_step if needed. */
export function deleteStep(def, name) {
  const { [name]: _removed, ...rest } = def.steps;
  const cleaned = {};
  for (const [k, v] of Object.entries(rest)) cleaned[k] = clearNext(v, name);
  const start =
    def.start_step === name ? Object.keys(cleaned)[0] ?? "" : def.start_step;
  return { ...def, start_step: start, steps: cleaned };
}

/**
 * Wire `source` → `target` (endpoint_call.next), cycle-safe. Branch routing is
 * condition-based, so connecting FROM a branch on the canvas is rejected.
 * Returns { ok: true, def } | { ok: false, error }.
 */
export function connectStep(def, source, target) {
  const step = def.steps[source];
  if (!step) return { ok: false, error: `Unknown step "${source}".` };
  if (step.type !== "endpoint_call") {
    return {
      ok: false,
      error: "Branch routing is set on the branch step, not by drawing on the canvas."
    };
  }
  if (source === target) return { ok: false, error: "A step can’t run after itself." };
  if (!def.steps[target]) return { ok: false, error: `Unknown step "${target}".` };
  if ((step.next || []).includes(target)) return { ok: true, def }; // already connected

  if (ancestorsOf(def.steps, source).has(target)) {
    return {
      ok: false,
      error: `Can’t connect — “${target}” already leads back to “${source}” (loop).`
    };
  }
  const candidate = {
    ...def,
    steps: {
      ...def.steps,
      [source]: { ...step, next: [...(step.next || []), target] }
    }
  };
  const cyc = findCycle(candidate);
  if (cyc) return { ok: false, error: `Can’t connect — that would create a loop: ${cyc}` };
  return { ok: true, def: candidate };
}

/**
 * Remove one edge by its canvas id (`${source}->${target}@${idx}` or
 * `${source}->${target}@default`).
 */
export function disconnectEdge(def, edgeId) {
  const arrow = edgeId.indexOf("->");
  const at = edgeId.lastIndexOf("@");
  if (arrow < 0 || at < 0) return def;
  const source = edgeId.slice(0, arrow);
  const suffix = edgeId.slice(at + 1);
  const step = def.steps[source];
  if (!step) return def;

  if (step.type === "endpoint_call") {
    const idx = parseInt(suffix, 10);
    if (Number.isNaN(idx)) return def;
    return {
      ...def,
      steps: {
        ...def.steps,
        [source]: { ...step, next: step.next.filter((_, i) => i !== idx) }
      }
    };
  }
  if (suffix === "default") {
    return {
      ...def,
      steps: { ...def.steps, [source]: { ...step, default_next: null } }
    };
  }
  const idx = parseInt(suffix, 10);
  if (Number.isNaN(idx)) return def;
  return {
    ...def,
    steps: {
      ...def.steps,
      [source]: { ...step, branches: step.branches.filter((_, i) => i !== idx) }
    }
  };
}
